import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { CancelOutlined, CheckCircleOutlined, CloseOutlined, SaveOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { FunctionComponent } from 'react'
import { INominalCodeMappingState, INominalReferenceData } from '../../../../hooks/use-nominal-code-mapping'
import { NominalBulkAction, NominalStatusFilter, mappingToOutcomeValue, pluralise } from '../../../../shared/utils/nominal-code-utils'
import { segmentedToggleSx } from '../../../../shared/utils/segmented-toggle-sx'
import { SearchBar } from '../../display-elements-components/search-bar'
import { NominalCodeMappingTable } from './nominal-code-mapping-table'
import { NominalOutcomeAutocomplete } from './nominal-outcome-autocomplete'
import { NominalOutcomeDrawer } from './nominal-outcome-drawer'

/**
 * NominalCodeMappingWorkspace
 * -------------------------------------------------------------------------
 * The working part of Chart of Accounts mapping: status filters with counts, search, "Accept N
 * suggestions", the bulk bar while rows are ticked, the table, and the Advanced drawer. Rendered by
 * the Chart of Accounts page and by the import step's inline section, each feeding it the state
 * from useNominalCodeMapping, so both behave the same. Each owns its own save bar
 * (NominalCodeSaveBar below) and everything around the table.
 *
 * Controls with nothing to act on are left out rather than shown empty: the no-spend checkbox when
 * every code has spend, the "Suggested" filter and "Accept N suggestions" when nothing is
 * suggested, and the row checkboxes and bulk bar outside the "Needs a decision" view.
 */

const STATUS_FILTER_LABELS: { value: NominalStatusFilter, label: string }[] = [
  { value: 'needsDecision', label: 'Needs a decision' },
  { value: 'suggested', label: 'Suggested' },
  { value: 'mapped', label: 'Mapped' },
  { value: 'all', label: 'All' },
]

const emptyViewMessage = (mapping: INominalCodeMappingState): string => {
  const { scopedRows, scope, searchQuery, showNoSpend, noSpendCount, statusFilter } = mapping
  if (scopedRows.length === 0 && scope === 'review') return 'None of the lines in review for this period carry a code yet.'
  if (scopedRows.length === 0 && scope === 'upload') return 'None of the lines in this upload carry a code.'
  if (searchQuery.trim()) return 'No code matches that search.'
  const hidden = !showNoSpend && noSpendCount > 0
    ? ` ${pluralise(noSpendCount, 'code has', 'codes have')} no spend yet and ${noSpendCount === 1 ? 'is' : 'are'} hidden.`
    : ''
  if (statusFilter === 'needsDecision') return `Nothing here needs a decision.${hidden}`
  if (statusFilter === 'suggested') return `No open suggestions here.${hidden}`
  if (statusFilter === 'mapped') return `No codes here have an outcome yet.${hidden}`
  return `No codes to show.${hidden}`
}

export const NominalCodeMappingWorkspace: FunctionComponent<{
  mapping: INominalCodeMappingState,
  reference: INominalReferenceData,
  // The summary request is in flight.
  isLoading: boolean,
  // A summary has been loaded at least once.
  hasSummary: boolean,
  // The "Show codes with no spend" switch. Off where every code was seen on a line anyway.
  showNoSpendToggle?: boolean,
  // Row actions for saved mappings. Omitted on the import step, where saved mappings are not managed.
  onDeleteMapping?: (code: string) => void,
  onEditMapping?: (code: string) => void,
}> = ({
  mapping,
  reference,
  isLoading,
  hasSummary,
  showNoSpendToggle = true,
  onDeleteMapping,
  onEditMapping,
}) => {
  const {
    statusFilter,
    setStatusFilter,
    filterCounts,
    searchQuery,
    setSearchQuery,
    showNoSpend,
    setShowNoSpend,
    noSpendCount,
    hasOpenSuggestions,
    acceptableSuggestionKeys,
    acceptAllSuggestionsHandler,
    selectedVisible,
    bulkAction,
    setBulkAction,
    bulkActivity,
    setBulkActivity,
    applyBulkHandler,
    allowClearSaved,
    setSelected,
    filteredRows,
    advancedKey,
    advancedRow,
    advancedEdit,
    closeAdvancedHandler,
  } = mapping

  const filters = STATUS_FILTER_LABELS.filter(filter => filter.value !== 'suggested' || hasOpenSuggestions)
  // The bulk bar works on codes still to decide, so only that view can tick rows.
  const selectable = statusFilter === 'needsDecision'

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }} flexWrap='wrap' useFlexGap>
        {/* Scrolls sideways on a phone rather than wrapping the segmented control. */}
        <Box sx={{ maxWidth: '100%', overflowX: 'auto', flexShrink: 0 }}>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            size='small'
            onChange={(_event, next: NominalStatusFilter | null) => { if (next) setStatusFilter(next) }}
            aria-label='Filter codes by status'
            sx={segmentedToggleSx}>
            {filters.map(filter => (
              <ToggleButton key={filter.value} value={filter.value} sx={{ whiteSpace: 'nowrap' }}>
                {filter.label} ({filterCounts[filter.value].toLocaleString()})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ flex: 1, minWidth: { md: 220 }, maxWidth: { md: 360 } }}>
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} placeholder='Search by code or account' />
        </Box>
        {showNoSpendToggle && noSpendCount > 0 && (
          <FormControlLabel
            control={<Checkbox size='small' checked={showNoSpend} onChange={(event) => setShowNoSpend(event.target.checked)} />}
            label={<Typography variant='body2'>Show codes with no spend ({noSpendCount.toLocaleString()})</Typography>}
          />
        )}
        <Box sx={{ flex: { md: 1 } }} />
        {hasOpenSuggestions && acceptableSuggestionKeys.length > 0 && (
          <Button
            variant='outlined'
            startIcon={<CheckCircleOutlined fontSize='small' />}
            onClick={acceptAllSuggestionsHandler}>
            <Typography sx={{ pl: 0.5 }}>Accept {pluralise(acceptableSuggestionKeys.length, 'suggestion', 'suggestions')}</Typography>
          </Button>
        )}
      </Stack>

      {/* Bulk bar, shown only while rows are ticked, so the table is uncluttered until there is
          something to act on. */}
      {selectable && selectedVisible.length > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} useFlexGap flexWrap='wrap'
          sx={{ p: 1.5, mb: 2, borderRadius: '10px', backgroundColor: 'lightGrey.light' }}>
          <Typography variant='body2' sx={{ color: 'text.primary' }}>{selectedVisible.length.toLocaleString()} selected</Typography>
          <Select
            size='small'
            value={bulkAction}
            onChange={(event: SelectChangeEvent<string>) => setBulkAction(event.target.value as NominalBulkAction)}
            inputProps={{ 'aria-label': 'Action for the selected codes' }}
            sx={{ minWidth: 220, color: 'text.primary' }}>
            <MenuItem value='exclude'>Exclude from emissions</MenuItem>
            <MenuItem value='private'>Don't import</MenuItem>
            <MenuItem value='activity'>Set activity</MenuItem>
            <MenuItem value='clear'>{allowClearSaved ? 'Clear outcome' : 'Undo changes'}</MenuItem>
          </Select>
          {bulkAction === 'activity' && (
            <Box sx={{ flex: 1, minWidth: 260, maxWidth: { md: 420 } }}>
              <NominalOutcomeAutocomplete
                id='bulk'
                activitiesOnly
                value={bulkActivity?.value ?? null}
                currentConversion={bulkActivity?.conversion ?? null}
                onChange={(_id, next, conversion) => setBulkActivity({ value: next, conversion })}
                activities={reference.activities}
                loading={reference.activitiesLoading}
                codeLabel='the selected codes' />
            </Box>
          )}
          <Button
            variant='contained'
            startIcon={<CheckCircleOutlined fontSize='small' />}
            disabled={bulkAction === 'activity' && !bulkActivity}
            onClick={applyBulkHandler}>
            <Typography sx={{ pl: 0.5 }}>Apply</Typography>
          </Button>
          <Button
            variant='text'
            startIcon={<CloseOutlined fontSize='small' />}
            onClick={() => setSelected([])}>
            <Typography sx={{ pl: 0.5 }}>Clear selection</Typography>
          </Button>
        </Stack>
      )}

      {!isLoading && hasSummary && filteredRows.length === 0 ? (
        <Alert
          severity={statusFilter === 'needsDecision' && !searchQuery.trim() ? 'success' : 'info'}
          sx={{ mb: 2 }}
          action={!showNoSpend && noSpendCount > 0 && !searchQuery.trim() ? (
            <Button color='inherit' size='small' onClick={() => setShowNoSpend(true)}>
              Show {noSpendCount === 1 ? 'it' : 'them'}
            </Button>
          ) : undefined}>
          {emptyViewMessage(mapping)}
        </Alert>
      ) : (
        <NominalCodeMappingTable
          rows={filteredRows}
          edits={mapping.edits}
          onChange={mapping.changeHandler}
          selected={mapping.selected}
          setSelected={mapping.setSelected}
          selectable={selectable}
          editingKeys={mapping.editingKeys}
          onStartEditing={mapping.startEditingHandler}
          onAcceptSuggestion={mapping.acceptSuggestionHandler}
          onOpenAdvanced={mapping.openAdvancedHandler}
          onUndoChange={mapping.undoChangeHandler}
          onDeleteMapping={onDeleteMapping}
          onEditMapping={onEditMapping}
          defaultSourceId={reference.defaultSourceId}
          activities={reference.activities}
          sources={reference.sources}
          groups={reference.groups}
          showSourceChip={reference.showSourceChip}
          activitiesLoading={reference.activitiesLoading}
          loading={isLoading && !hasSummary}
        />
      )}

      {advancedKey && (
        <NominalOutcomeDrawer
          open={!!advancedKey}
          nominalCode={advancedRow?.nominalCode || advancedEdit?.nominalCode || advancedKey}
          accountName={advancedRow?.nominalDescription ?? advancedEdit?.nominalDescription}
          value={advancedEdit ? advancedEdit.value : mappingToOutcomeValue(advancedRow?.mapping)}
          defaultSourceId={reference.defaultSourceId}
          activities={reference.activities}
          sources={reference.sources}
          groups={reference.groups}
          onApply={(next, conversion) => {
            mapping.changeHandler(advancedKey, next, conversion)
            closeAdvancedHandler()
          }}
          onClose={closeAdvancedHandler}
        />
      )}
    </>
  )
}

/**
 * What a save would change and the button that saves it. The page shows it only while there is
 * something unsaved; the import step keeps it in view while its section is open.
 */
export const NominalCodeSaveBar: FunctionComponent<{
  counts: INominalCodeMappingState['footerCounts'],
  isSaving: boolean,
  onSave: () => void,
  onDiscard: () => void,
  saveLabel: string,
}> = ({ counts, isSaving, onSave, onDiscard, saveLabel }) => {
  const hasChanges = counts.total > 0
  return (
    <Paper
      variant='outlined'
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        mt: 2,
        p: 2,
        borderRadius: '10px',
        boxShadow: '0 -4px 16px rgba(17, 30, 33, 0.08)',
      }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent='space-between'>
        <Typography variant='body2' color={hasChanges ? 'text.primary' : 'text.secondary'}>
          {hasChanges
            ? [
              pluralise(counts.total, 'unsaved change', 'unsaved changes'),
              `${counts.categorised.toLocaleString()} categorised`,
              `${counts.excluded.toLocaleString()} excluded`,
              ...(counts.notImported > 0 ? [`${counts.notImported.toLocaleString()} not imported`] : []),
              ...(counts.cleared > 0 ? [`${counts.cleared.toLocaleString()} cleared`] : []),
            ].join(' · ')
            : 'No unsaved changes. Choose an outcome for a code to save it.'}
        </Typography>
        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            variant='outlined'
            disabled={isSaving || !hasChanges}
            startIcon={<CancelOutlined fontSize='small' />}
            onClick={onDiscard}>
            <Typography sx={{ pl: 0.5 }}>Discard</Typography>
          </Button>
          <LoadingButton
            variant='contained'
            loading={isSaving}
            loadingPosition='start'
            disabled={!hasChanges}
            startIcon={<SaveOutlined fontSize='small' />}
            onClick={onSave}>
            <Typography sx={{ pl: 0.5 }}>{isSaving ? 'Saving' : saveLabel}</Typography>
          </LoadingButton>
        </Stack>
      </Stack>
    </Paper>
  )
}
